/**
 * Fix-and-resubmit recovery for failed visit_sync_queue rows (#2082).
 *
 * When a queued write fails on a CORRECTABLE server validation (mileage-same,
 * drive/call-time problems, odometer-pair), the rep needs a way to correct the
 * offending field and resubmit — not just Retry (re-fails) or Discard (data
 * loss). This module supplies the pure pieces of that path:
 *
 *   - isCorrectable(failedReason): is this failure one the rep can fix here?
 *   - failedOpToEditForm(queueRow): map a failed checkIn row → draft form state.
 *
 * Phase 1 (#2082) targets the check-in create case (diag #47 "Start miles and
 * End miles are the same"). The classifier itself is general — it keys on the
 * server messages from apps/projects/apis/serializer.py validate().
 */

import type { QueueRow } from './types';
import type { CheckInInput } from './ops/checkIn';
import type { CreateManualVisitInput } from './ops/createManualVisit';
import { STORES, getAll, remove } from '../utils/indexedDB';
import { sanitizeDriveData, clearLoneOdometer } from '../utils/driveSegment';

/** Actions the fix-and-resubmit path can reopen and correct. */
export type CorrectableAction = 'checkIn' | 'createManualVisit';
export type CorrectablePayload = CheckInInput | CreateManualVisitInput;

/**
 * Server validation fragments the rep can correct in the Edit Visit form.
 * Substring-matched so a joined multi-error reason ("A, B") still classifies.
 * Keep in sync with serializer.py validate() field-level errors.
 */
const CORRECTABLE_FRAGMENTS = [
  'Start miles and End miles are the same',
  'Start miles is greater than End miles',
  'Both Start and End miles must be entered together',
  'Start and End Drive time not selected properly',
  'Start and End Drive time cannot be the same',
  'Start and End Call time not selected properly',
  'Start and End Call time cannot be the same',
  'Drive Time - Call Time conflict',
  'Start Drive time is required when End Drive time is provided',
  'Start Call time is required when End Call time is provided',
] as const;

/**
 * True when `failedReason` is a known field-level validation the rep can fix
 * and resubmit. Network/auth/5xx/unknown reasons are NOT correctable — those
 * keep Retry/Discard as the only actions.
 */
export function isCorrectable(failedReason: string | null | undefined): boolean {
  if (!failedReason) return false;
  return CORRECTABLE_FRAGMENTS.some((fragment) => failedReason.includes(fragment));
}

/**
 * Draft Edit Visit form state seeded from a failed check-in queue row. Carries
 * the rep-editable display fields (time-in, odometer, notes) AND the original
 * payload so a corrected check-in op can be rebuilt on save without re-deriving
 * store/project/associate/gps.
 */
export interface DraftEditForm {
  /** visit_sync_queue primary key — removed when the corrected op is enqueued. */
  queueRowId: number;
  action: CorrectableAction;
  storeName: string;
  failedReason?: string;
  /** Visit date (YYYY-MM-DD) for the header. */
  dteCall: string;
  /** Check-in / visit time as HH:MM (parsed from stCallTime). */
  timeIn: string;
  /** Drive-start odometer as a string for the number input ('' when none). */
  driveStartOdo: string;
  /** Drive-end odometer (the at-store reading) as a string ('' when none). */
  driveEndOdo: string;
  /** Editable notes (a manual entry carries them; check-in exposes the field). */
  notes: string;
  /** Optimistic visit_task_sessions uuid of the failed op. For a manual visit
   * this is REUSED on resubmit so queued dependents stay linked; for a check-in
   * it's removed (a new localId is minted). */
  relatedTaskUUID?: string;
  /** Original failed payload, preserved to rebuild a corrected op. */
  payload: CorrectablePayload;
}

/** Extract HH:MM from a naive-local datetime ('YYYY-MM-DDTHH:MM:SS') or ''. */
function timeInFromStCall(stCallTime: string | undefined): string {
  if (!stCallTime) return '';
  const tIndex = stCallTime.indexOf('T');
  if (tIndex === -1) return stCallTime.slice(0, 5);
  return stCallTime.slice(tIndex + 1, tIndex + 6);
}

function odoToString(value: number | undefined): string {
  return value === undefined || value === null ? '' : String(value);
}

/**
 * Map a failed queue row into the draft Edit Visit form state. Two actions are
 * reopened (#2082 Phase 1 = checkIn; #3429 = createManualVisit, the manual /
 * after-the-fact entry that shares the same server odometer/time validations):
 *   - checkIn: odometer from driveData.start/endOdometer or the odometer-only
 *     pair (startOdometer / odometer); no notes.
 *   - createManualVisit: odometer from driveOdoStart/driveOdoEnd; carries notes.
 *
 * Throws for any other action — editVisit/PATCH reopen remains a later follow-up.
 */
export function failedOpToEditForm(row: QueueRow<CorrectablePayload>): DraftEditForm {
  if (row.action === 'createManualVisit') {
    const payload = row.payload as CreateManualVisitInput;
    return {
      queueRowId: row.id as number,
      action: 'createManualVisit',
      storeName: payload.storeName,
      failedReason: row.failedReason,
      dteCall: payload.dteCall,
      timeIn: timeInFromStCall(payload.stCallTime),
      driveStartOdo: odoToString(payload.driveOdoStart),
      driveEndOdo: odoToString(payload.driveOdoEnd),
      notes: payload.notes ?? '',
      relatedTaskUUID: row.relatedTaskUUID,
      payload,
    };
  }

  if (row.action !== 'checkIn') {
    throw new Error(`failedOpToEditForm: unsupported action "${row.action}"`);
  }
  const payload = row.payload as CheckInInput;
  const driveStartOdo = odoToString(payload.driveData?.startOdometer ?? payload.startOdometer);
  const driveEndOdo = odoToString(payload.driveData?.endOdometer ?? payload.odometer);

  return {
    queueRowId: row.id as number,
    action: 'checkIn',
    storeName: payload.storeName,
    failedReason: row.failedReason,
    dteCall: payload.dteCall,
    timeIn: timeInFromStCall(payload.stCallTime),
    driveStartOdo,
    driveEndOdo,
    notes: '',
    relatedTaskUUID: row.relatedTaskUUID,
    payload,
  };
}

/** Parse an odometer string input to a number, or undefined when blank/invalid. */
function parseOdo(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const n = parseInt(trimmed, 10);
  return Number.isNaN(n) ? undefined : n;
}

/**
 * Rebuild a corrected CheckInInput from the edited draft form, re-running the
 * same client guards the check-in write path uses so the resubmit can't
 * reproduce the rejection:
 *   - a lone odometer (only start OR only end) is dropped — the server requires
 *     the pair together.
 *   - on a driveData segment, sanitizeDriveData (#1991) drops an inverted/
 *     zero-length or implausible time interval.
 * An equal pair the rep re-affirmed in the Fix form is carried and records as
 * a zero-mile drive (#3618).
 * The edited time-in is folded back into stCallTime (naive-local, seconds :00).
 */
export function buildCorrectedCheckIn(form: DraftEditForm): CheckInInput {
  const base = form.payload as CheckInInput;
  const start = parseOdo(form.driveStartOdo);
  const end = parseOdo(form.driveEndOdo);
  // The server rejects a lone odometer; only carry the pair when both are set.
  const bothOdo = start !== undefined && end !== undefined;
  const pairStart = bothOdo ? start : undefined;
  const pairEnd = bothOdo ? end : undefined;

  const stCallTime = form.timeIn
    ? `${form.dteCall}T${form.timeIn.slice(0, 5)}:00`
    : base.stCallTime;

  if (base.driveData) {
    const sanitized = sanitizeDriveData({
      startTime: new Date(base.driveData.startTime),
      endTime: new Date(base.driveData.endTime),
      startOdometer: pairStart,
      endOdometer: pairEnd,
    });
    const driveData = sanitized
      ? {
          startTime: base.driveData.startTime,
          endTime: base.driveData.endTime,
          startOdometer: sanitized.startOdometer,
          endOdometer: sanitized.endOdometer,
        }
      : undefined;
    return { ...base, stCallTime, driveData };
  }

  // Odometer-only shape: the pair-completeness rule above already dropped a
  // lone reading; an equal pair records as a zero-mile drive (#3618).
  return { ...base, stCallTime, startOdometer: pairStart, odometer: pairEnd };
}

/**
 * Draft Save: enqueue the corrected check-in op, then remove the failed queue
 * row and its optimistic visit_task_sessions row (same cleanup Discard does, so
 * no phantom task lingers). Returns the new localId. The enqueue happens FIRST
 * so the corrected op's optimistic row exists before the old one is removed.
 */
export async function resubmitCorrectedCheckIn(form: DraftEditForm): Promise<string> {
  const corrected = buildCorrectedCheckIn(form);
  const { enqueueOp } = await import('./writeOps');
  const newLocalId = await enqueueOp('checkIn', corrected);

  await remove(STORES.VISIT_SYNC_QUEUE, form.queueRowId);
  if (form.relatedTaskUUID) {
    const tasks = (await getAll(STORES.VISIT_TASK_SESSIONS)) as Array<{ uuid?: string }>;
    if (tasks.some((t) => t.uuid === form.relatedTaskUUID)) {
      await remove(STORES.VISIT_TASK_SESSIONS, form.relatedTaskUUID);
    }
  }
  // Tell the failed-sync banner / Sync Issues list to re-count (Discard parity, #2080).
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('rdm:sync-enqueued'));
  }
  return newLocalId;
}

/**
 * Rebuild a corrected CreateManualVisitInput from the edited draft form (#3429).
 * Everything not editable in the Fix form (drive-to/back times, drive-back odo,
 * team flags, off-schedule reason prefix) is carried straight from the original
 * payload; only time-in, drive-to odometer, and notes are overlaid. The drive-to
 * odometer pair runs through clearLoneOdometer so a half-entered pair can't
 * reproduce a server rejection; an equal pair records as zero miles (#3618).
 */
export function buildCorrectedManualVisit(form: DraftEditForm): CreateManualVisitInput {
  const base = form.payload as CreateManualVisitInput;
  const { startOdometer, endOdometer } = clearLoneOdometer(
    parseOdo(form.driveStartOdo),
    parseOdo(form.driveEndOdo),
  );
  const stCallTime = form.timeIn ? `${base.dteCall}T${form.timeIn.slice(0, 5)}:00` : base.stCallTime;
  return {
    ...base,
    stCallTime,
    driveOdoStart: startOdometer,
    driveOdoEnd: endOdometer,
    notes: form.notes.trim() ? form.notes : undefined,
  };
}

/**
 * Resubmit a corrected manual visit (#3429). Unlike the check-in path, the
 * corrected op is re-enqueued under the SAME localId (the original
 * relatedTaskUUID / idempotency key) so that queued dependents — the visit's
 * uploadPhoto / submitSurvey rows, which key on `dependsOn === this localId` —
 * stay linked and drain once the corrected parent finally syncs. The old failed
 * queue row is removed; the optimistic visit_task_sessions row is left in place
 * (enqueueOp.applyLocal refreshes it under the same uuid).
 */
export async function resubmitCorrectedManualVisit(form: DraftEditForm): Promise<string> {
  const corrected = buildCorrectedManualVisit(form);
  const { enqueueOp } = await import('./writeOps');
  const newLocalId = form.relatedTaskUUID
    ? await enqueueOp('createManualVisit', corrected, form.relatedTaskUUID)
    : await enqueueOp('createManualVisit', corrected);

  await remove(STORES.VISIT_SYNC_QUEUE, form.queueRowId);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('rdm:sync-enqueued'));
  }
  return newLocalId;
}

/** Resubmit a corrected draft, dispatching on its action (#3429). */
export function resubmitCorrectedDraft(form: DraftEditForm): Promise<string> {
  return form.action === 'createManualVisit'
    ? resubmitCorrectedManualVisit(form)
    : resubmitCorrectedCheckIn(form);
}
